import React from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Card,
  Alert,
  SimpleGrid,
  Stat,
  Progress,
  Table,
} from '@chakra-ui/react';
import { Thead, Tbody, Tr, Th, Td } from '@/components/ui/chakra-v2-compat';
import { formatYmdEs } from './MetricasInformeContenido.jsx';
import {
  MotionBox,
  COL_ASIS,
  LABEL_ESTADO_ENSAYO,
  LABEL_ESTADO_ASIS,
  LABEL_ESTADO_ACTUACION,
  LABEL_AVAL_STATUS,
  MiniDistribucionAsist,
  formatPct,
  nombreMusico,
  LABEL_DELTA_KPI,
  formatDeltaLine,
  HEX_EST_ENSAYO,
  HEX_EST_ASIS,
  HEX_EST_ACT,
  BORDE_EST_ENSAYO,
  BORDE_EST_ACT,
  HEX_AVAL,
  toPieData,
  toPieDataWithKeyColors,
  RenderPie,
  barDataFromObras,
  barDataFromAutores,
  barDataSedesGrupos,
  BarHorizMini,
  ListDistribAnimada,
  SeccionCaja,
} from './metricas-informe-parts.jsx';

/** Tablas en resumen oscuro: sin `striped`+colorScheme (fondos claros + texto claro = ilegible). */
const tableOscuroResumen = {
  'tbody tr:nth-of-type(odd)': { bg: 'whiteAlpha.50' },
  'tbody tr:hover': { bg: 'whiteAlpha.100' },
};

/**
 * Sección a pantalla completa estilo Spotify Wrapped: título en mayúsculas,
 * cuerpo de texto y dato “hero”; anima al entrar en viewport.
 */
function SpotifyScrollSection({ kicker, expl, bigNumber, children, minH = { base: 'auto', md: 'min(90vh, auto)' } }) {
  return (
    <Box
      as="section"
      minH={minH}
      py={{ base: 10, md: 14 }}
      px={{ base: 2, md: 4 }}
      scrollSnapAlign="start"
      scrollMarginTop="80px"
    >
      <MotionBox
        maxW="5xl"
        mx="auto"
        initial={{ opacity: 0, y: 48 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.18, margin: '0px 0px -12% 0px' }}
        transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
      >
        {kicker ? (
          <Text
            fontSize="xs"
            fontWeight="800"
            letterSpacing="0.28em"
            textTransform="uppercase"
            color="purple.300"
            mb={2}
          >
            {kicker}
          </Text>
        ) : null}
        {bigNumber != null && bigNumber !== false ? (
          <Text
            fontSize={{ base: '2.8rem', md: '4rem' }}
            fontWeight="900"
            lineHeight="0.92"
            letterSpacing="-0.04em"
            bgGradient="linear(to-br, #faf5ff, #a78bfa, #2dd4bf)"
            bgClip="text"
            color="transparent"
            mb={4}
          >
            {typeof bigNumber === 'number' ? new Intl.NumberFormat('es-ES').format(bigNumber) : String(bigNumber)}
          </Text>
        ) : null}
        {expl ? (
          <Text
            color="whiteAlpha.800"
            fontSize={{ base: 'md', md: 'lg' }}
            lineHeight="tall"
            maxW="3xl"
            mb={6}
            fontWeight="500"
          >
            {expl}
          </Text>
        ) : null}
        <Box>{children}</Box>
      </MotionBox>
    </Box>
  );
}

/**
 * Cuerpo del resumen compartible: mismo orden y mismos datos que el informe en Estadísticas,
 * presentado en “historias” al hacer scroll, animadas a la entrada.
 */
export default function MetricasSpotifyResumenCuerpo({
  data,
  tMetricas,
  sub,
  vistaGraficos,
  incluirNarrativa,
}) {
  const s = tMetricas.wrapped.story;
  const d = data?.resumen;
  if (!d || !data.ok || data.vacio) return null;
  return (
    <VStack align="stretch" gap={0} position="relative" zIndex={1}>
      {incluirNarrativa && (
        <SpotifyScrollSection
          kicker={s.kickerIa}
          expl={s.explIa}
          minH={{ base: 'auto', md: 'min(70vh, auto)' }}
        >
          <Alert.Root
            status={data.narrativaIa ? 'info' : 'warning'}
            borderRadius="xl"
            borderLeftWidth="4px"
            borderLeftColor={data.narrativaIa ? 'cyan.400' : 'orange.400'}
            bg="blackAlpha.400"
            borderWidth="1px"
            borderColor="whiteAlpha.200"
            variant='subtle'
            borderStartWidth='3px'
            borderStartColor='colorPalette.solid'>
            <Alert.Indicator />
            <Box>
              <Text fontWeight="700" fontSize="sm" mb={data.narrativaIa ? 1 : 0} color="white">
                {data.narrativaIa ? tMetricas.wrapped.sectionIa : 'Narrativa con IA no disponible'}
              </Text>
              {data.narrativaIa ? (
                <Text fontSize="sm" color="whiteAlpha.800" whiteSpace="pre-line">
                  {data.narrativaIa}
                </Text>
              ) : (
                <Text fontSize="sm" color="whiteAlpha.800">
                  No se ha generado texto. Puede faltar la clave de la API de IA en el servidor, o haber
                  ocurrido un error temporal. Vuelve a consultar las estadísticas o pide ayuda a administración.
                </Text>
              )}
            </Box>
          </Alert.Root>
        </SpotifyScrollSection>
      )}

      <SpotifyScrollSection
        kicker={s.kickerCifras}
        expl={s.explCifras}
        minH={{ base: '30vh', md: '40vh' }}
        bigNumber={null}
      >
        <Box h="1px" bg="whiteAlpha.200" w="30%" maxW="200px" />
      </SpotifyScrollSection>

      <SpotifyScrollSection
        kicker={s.kickerEnsayos}
        expl={s.explEnsayos}
        bigNumber={d.ensayos.total}
      >
        <SeccionCaja titulo="Ensayos" tituloColor="purple.500">
          <Stat.Root size="sm">
            <Stat.Label>Total con fecha en el rango</Stat.Label>
            <Stat.ValueText>{d.ensayos.total}</Stat.ValueText>
          </Stat.Root>
          <Stat.Root size="sm" mt={2}>
            <Stat.Label>Horas (sólo con hora de fin)</Stat.Label>
            <Stat.ValueText>{d.ensayos.horasSuma} h</Stat.ValueText>
            {d.ensayos.ensayosSinHoraFin > 0 && (
              <Stat.HelpText>
                {d.ensayos.ensayosSinHoraFin} ensayo(s) sin hora de fin (no suman)
              </Stat.HelpText>
            )}
          </Stat.Root>
          <Text fontSize="xs" color={sub} mt={3} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
            Por estado
          </Text>
          {vistaGraficos ? (
            <RenderPie
              data={toPieDataWithKeyColors(d.ensayos.porEstado, LABEL_ESTADO_ENSAYO, HEX_EST_ENSAYO)}
              height={210}
            />
          ) : (
            <ListDistribAnimada
              record={d.ensayos.porEstado}
              labelMap={LABEL_ESTADO_ENSAYO}
              colorOfKey={(k) => BORDE_EST_ENSAYO[k] || 'purple.400'}
              muted={sub}
            />
          )}
        </SeccionCaja>
      </SpotifyScrollSection>

      <SpotifyScrollSection
        kicker={s.kickerAsis}
        expl={s.explAsis}
        bigNumber={
          d.asistenciaEnsayos.tasaPresenteSobreCerrado != null
            ? formatPct(d.asistenciaEnsayos.tasaPresenteSobreCerrado)
            : d.asistenciaEnsayos.totalRegistros
        }
      >
        <SeccionCaja titulo="Asistencia a ensayos" tituloColor="green.500">
          <Stat.Root size="sm">
            <Stat.Label>Registros (músico × ensayo)</Stat.Label>
            <Stat.ValueText>{d.asistenciaEnsayos.totalRegistros}</Stat.ValueText>
            {d.asistenciaEnsayos.tasaPresenteSobreCerrado != null && (
              <Stat.HelpText>
                {formatPct(d.asistenciaEnsayos.tasaPresenteSobreCerrado)} presente sobre cerrado (presente /
                presente+ausente+justificado)
              </Stat.HelpText>
            )}
          </Stat.Root>
          {d.asistenciaEnsayos.tasaPresenteSobreCerrado != null && (
            <Progress.Root
              value={d.asistenciaEnsayos.tasaPresenteSobreCerrado * 100}
              size="sm"
              colorPalette="green"
              borderRadius="md"
              mt={1}
              maxW="100%"
              bg="whiteAlpha.200">
              <Progress.Track>
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
          )}
          {!vistaGraficos && (
            <>
              <Text fontSize="xs" color={sub} mt={2} fontWeight="600">
                Distribución
              </Text>
              <MiniDistribucionAsist porEstado={d.asistenciaEnsayos.porEstado} />
            </>
          )}
          <Text fontSize="xs" color={sub} mt={3} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
            Por estado
          </Text>
          {vistaGraficos ? (
            <RenderPie
              data={toPieDataWithKeyColors(d.asistenciaEnsayos.porEstado, LABEL_ESTADO_ASIS, HEX_EST_ASIS)}
              height={220}
            />
          ) : (
            <ListDistribAnimada
              record={d.asistenciaEnsayos.porEstado}
              labelMap={LABEL_ESTADO_ASIS}
              colorOfKey={(k) => COL_ASIS[k] || 'gray.400'}
              muted={sub}
            />
          )}
        </SeccionCaja>
      </SpotifyScrollSection>

      <SpotifyScrollSection
        kicker={s.kickerActu}
        expl={s.explActu}
        bigNumber={d.actuaciones.total}
      >
        <SeccionCaja titulo="Actuaciones" tituloColor="blue.500">
          <Stat.Root size="sm">
            <Stat.Label>Total con fecha en el rango</Stat.Label>
            <Stat.ValueText>{d.actuaciones.total}</Stat.ValueText>
          </Stat.Root>
          <Text fontSize="xs" color={sub} mt={3} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
            Por estado
          </Text>
          {vistaGraficos ? (
            <RenderPie
              data={toPieDataWithKeyColors(d.actuaciones.porEstado, LABEL_ESTADO_ACTUACION, HEX_EST_ACT)}
              height={240}
            />
          ) : (
            <ListDistribAnimada
              record={d.actuaciones.porEstado}
              labelMap={LABEL_ESTADO_ACTUACION}
              colorOfKey={(k) => BORDE_EST_ACT[k] || 'blue.400'}
              muted={sub}
            />
          )}
        </SeccionCaja>
      </SpotifyScrollSection>

      {d.repertorioActuaciones && (
        <SpotifyScrollSection kicker={s.kickerRepertorio} expl={s.explRepertorio} bigNumber={false}>
          <SeccionCaja
            titulo="Repertorio en actuaciones (programas)"
            tituloColor="blue.700"
            descripcion="Cada obra en un programa cuenta una vez. Marchas con datos del catálogo."
            muted={sub}
          >
            <Text fontSize="xs" color={sub} fontWeight="600" mb={3}>
              Actuaciones con al menos una obra: {d.repertorioActuaciones.actuacionesAnalizadas}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <Box>
                <Text fontSize="sm" fontWeight="700" mb={2} color="blue.600">
                  Obras más en programas
                </Text>
                {(d.repertorioActuaciones.obras || []).length > 0 ? (
                  vistaGraficos ? (
                    <BarHorizMini data={barDataFromObras(d.repertorioActuaciones.obras)} h={240} />
                  ) : (
                    <Table.Root
                      size="sm"
                      variant="simple"
                      borderRadius="lg"
                      overflow="hidden"
                      sx={tableOscuroResumen}
                    >
                      <Table.Header bg="whiteAlpha.100">
                        <Table.Row>
                          <Table.ColumnHeader color="whiteAlpha.900">Obra</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.900">Autor</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.900" textAlign='end'>Apar.</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {d.repertorioActuaciones.obras.map((o) => (
                          <Table.Row key={o.clave}>
                            <Table.Cell fontWeight="500" color="whiteAlpha.900">{o.titulo}</Table.Cell>
                            <Table.Cell fontSize="sm" color="whiteAlpha.700">
                              {o.autor ?? '—'}
                            </Table.Cell>
                            <Table.Cell fontWeight="700" color="whiteAlpha.900" textAlign='end'>
                              {o.apariciones}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  )
                ) : (
                  <Text fontSize="sm" color={sub}>
                    Sin obras en repertorios de actuaciones en este periodo.
                  </Text>
                )}
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="700" mb={2} color="blue.600">
                  Autores más interpretados
                </Text>
                {(d.repertorioActuaciones.autores || []).length > 0 ? (
                  vistaGraficos ? (
                    <BarHorizMini data={barDataFromAutores(d.repertorioActuaciones.autores)} h={220} />
                  ) : (
                    <Table.Root
                      size="sm"
                      variant="simple"
                      borderRadius="lg"
                      overflow="hidden"
                      sx={tableOscuroResumen}
                    >
                      <Table.Header bg="whiteAlpha.100">
                        <Table.Row>
                          <Table.ColumnHeader color="whiteAlpha.900">Autor</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.900" textAlign='end'>Apar. en obras</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {d.repertorioActuaciones.autores.map((a, idx) => (
                          <Table.Row key={`${a.autor}-${idx}`}>
                            <Table.Cell fontWeight="500" color="whiteAlpha.900">{a.autor}</Table.Cell>
                            <Table.Cell fontWeight="700" color="whiteAlpha.900" textAlign='end'>
                              {a.apariciones}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  )
                ) : (
                  <Text fontSize="sm" color={sub}>
                    Sin autores (sin autor en obras o solo marchas sin autor en catálogo).
                  </Text>
                )}
              </Box>
            </SimpleGrid>
          </SeccionCaja>
        </SpotifyScrollSection>
      )}

      {d.encuestasAsistenciaActuaciones && (
        <SpotifyScrollSection
          kicker={s.kickerEncuestas}
          expl={s.explEncuestas}
          bigNumber={d.encuestasAsistenciaActuaciones.totalRespuestas}
        >
          <SeccionCaja titulo="Encuestas (asistencia a actuación)" tituloColor="cyan.600">
            <Text fontSize="xs" color={sub} fontWeight="600" mb={2}>
              Actuaciones con encuesta en el rango: {d.encuestasAsistenciaActuaciones.actuacionesConEncuesta}
            </Text>
            <Stat.Root size="sm">
              <Stat.Label>Respuestas en el periodo</Stat.Label>
              <Stat.ValueText>{d.encuestasAsistenciaActuaciones.totalRespuestas}</Stat.ValueText>
              {d.encuestasAsistenciaActuaciones.tasaSobreRespuestaBinaria != null && (
                <Stat.HelpText>
                  {formatPct(d.encuestasAsistenciaActuaciones.tasaSobreRespuestaBinaria)} asiste (sí / sí+no)
                </Stat.HelpText>
              )}
            </Stat.Root>
            {vistaGraficos ? (
              <RenderPie
                data={toPieData(
                  {
                    Sí: d.encuestasAsistenciaActuaciones.asisteSi,
                    No: d.encuestasAsistenciaActuaciones.asisteNo,
                    'Sin contestar': d.encuestasAsistenciaActuaciones.sinContestar,
                  },
                  { Sí: 'Sí', No: 'No', 'Sin contestar': 'Sin contestar' },
                )}
                height={200}
              />
            ) : (
              <ListDistribAnimada
                record={{
                  Sí: d.encuestasAsistenciaActuaciones.asisteSi,
                  No: d.encuestasAsistenciaActuaciones.asisteNo,
                  'Sin contestar': d.encuestasAsistenciaActuaciones.sinContestar,
                }}
                labelMap={{ Sí: 'Sí', No: 'No', 'Sin contestar': 'Sin contestar' }}
                colorOfKey={(k) => (k === 'Sí' ? 'green.400' : k === 'No' ? 'red.400' : 'gray.400')}
                muted={sub}
              />
            )}
            {d.pulidoFase7?.encuestasTasaRespuesta && (
              <Text fontSize="sm" color={sub} mt={2}>
                {tMetricas.fase7.tasaRespuestaTitle}:{' '}
                {d.pulidoFase7.encuestasTasaRespuesta.tasa != null
                  ? formatPct(d.pulidoFase7.encuestasTasaRespuesta.tasa)
                  : '—'}
                <Text as="span" fontSize="xs" display="block" mt={1}>
                  {tMetricas.fase7.tasaRespuestaHelp} · {d.pulidoFase7.encuestasTasaRespuesta.conRespuesta} /{' '}
                  {d.pulidoFase7.encuestasTasaRespuesta.invitaciones}
                </Text>
              </Text>
            )}
          </SeccionCaja>
        </SpotifyScrollSection>
      )}

      {d.archivoObrasPartituras && (
        <SpotifyScrollSection
          kicker={s.kickerArchivo}
          expl={s.explArchivo}
          bigNumber={d.archivoObrasPartituras.obrasCreadas}
        >
          <SeccionCaja titulo="Archivo de partituras" tituloColor="pink.500">
            <Text fontSize="md" fontWeight="600">
              Obras creadas en el periodo:{' '}
              <Text as="span" color="purple.500" fontSize="2xl" fontWeight="800">
                {d.archivoObrasPartituras.obrasCreadas}
              </Text>
            </Text>
            <Text fontSize="md" fontWeight="600" mt={2}>
              Páginas añadidas en el periodo:{' '}
              <Text as="span" color="pink.500" fontSize="2xl" fontWeight="800">
                {d.archivoObrasPartituras.paginasCreadas}
              </Text>
            </Text>
            <Text fontSize="xs" color={sub} mt={2}>
              Por fecha de creación en el sistema (no por fecha de interpretación).
            </Text>
          </SeccionCaja>
        </SpotifyScrollSection>
      )}

      {d.plantillaEnsayos && (
        <SpotifyScrollSection kicker={s.kickerPlantilla} expl={s.explPlantilla} bigNumber={d.plantillaEnsayos.musicosActivos}>
          <SeccionCaja titulo="Plantilla en ensayos" tituloColor="orange.500">
            <Text fontSize="sm" fontWeight="600">Músicos activos: {d.plantillaEnsayos.musicosActivos}</Text>
            <Text fontSize="sm" fontWeight="600" mt={2}>
              Con al menos un «Presente» en el rango: {d.plantillaEnsayos.musicosConPresencia}
            </Text>
            {d.plantillaEnsayos.ratioPresencia != null && (
              <>
                <Progress.Root
                  value={d.plantillaEnsayos.ratioPresencia * 100}
                  size="sm"
                  colorPalette="orange"
                  borderRadius="md"
                  mt={3}
                  maxW="100%"
                  striped
                  animated
                  bg="whiteAlpha.200">
                  <Progress.Track>
                    <Progress.Range />
                  </Progress.Track>
                </Progress.Root>
                <Text fontSize="xs" color={sub} mt={1}>
                  {formatPct(d.plantillaEnsayos.ratioPresencia)} de la plantilla con presencia registrada al menos
                  un día
                </Text>
              </>
            )}
          </SeccionCaja>
        </SpotifyScrollSection>
      )}

      {d.pulidoFase7 && (
        <SpotifyScrollSection kicker={tMetricas.fase7.cardTitle} expl={tMetricas.fase7.cardSubtitle} bigNumber={false}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <SeccionCaja titulo={tMetricas.fase7.sedesTitle} tituloColor="teal.600">
              {(d.pulidoFase7.sedesMasUsadas || []).length > 0 ? (
                vistaGraficos ? (
                  <BarHorizMini
                    data={barDataSedesGrupos(d.pulidoFase7.sedesMasUsadas)}
                    h={Math.min(56 + d.pulidoFase7.sedesMasUsadas.length * 28, 300)}
                  />
                ) : (
                  <Table.Root
                    size="sm"
                    variant="simple"
                    borderRadius="lg"
                    overflow="hidden"
                    sx={tableOscuroResumen}
                  >
                    <Table.Header bg="whiteAlpha.100">
                      <Table.Row>
                        <Table.ColumnHeader color="whiteAlpha.900">Sede</Table.ColumnHeader>
                        <Table.ColumnHeader color="whiteAlpha.900" textAlign='end'>Ensayos</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {d.pulidoFase7.sedesMasUsadas.map((x) => (
                        <Table.Row key={x.id}>
                          <Table.Cell fontWeight="500" color="whiteAlpha.900">{x.nombre}</Table.Cell>
                          <Table.Cell fontWeight="700" color="whiteAlpha.900" textAlign='end'>{x.ensayos}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                )
              ) : (
                <Text fontSize="sm" color={sub}>{tMetricas.fase7.emptySedes}</Text>
              )}
            </SeccionCaja>
            <SeccionCaja titulo={tMetricas.fase7.gruposTitle} tituloColor="purple.600">
              {(d.pulidoFase7.gruposMasUsados || []).length > 0 ? (
                vistaGraficos ? (
                  <BarHorizMini
                    data={barDataSedesGrupos(d.pulidoFase7.gruposMasUsados)}
                    h={Math.min(56 + d.pulidoFase7.gruposMasUsados.length * 28, 300)}
                  />
                ) : (
                  <Table.Root
                    size="sm"
                    variant="simple"
                    borderRadius="lg"
                    overflow="hidden"
                    sx={tableOscuroResumen}
                  >
                    <Table.Header bg="whiteAlpha.100">
                      <Table.Row>
                        <Table.ColumnHeader color="whiteAlpha.900">Grupo</Table.ColumnHeader>
                        <Table.ColumnHeader color="whiteAlpha.900" textAlign='end'>Ensayos</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {d.pulidoFase7.gruposMasUsados.map((g) => (
                        <Table.Row key={g.id}>
                          <Table.Cell fontWeight="500" color="whiteAlpha.900">{g.nombre}</Table.Cell>
                          <Table.Cell fontWeight="700" color="whiteAlpha.900" textAlign='end'>{g.ensayos}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                )
              ) : (
                <Text fontSize="sm" color={sub}>{tMetricas.fase7.emptyGrupos}</Text>
              )}
            </SeccionCaja>
          </SimpleGrid>
        </SpotifyScrollSection>
      )}

      {d.pulidoFase7 && (
        <SpotifyScrollSection kicker={s.kickerAvales} expl={s.explAvales} bigNumber={d.pulidoFase7.avalesCreadosEnRango.creadosEnRango || 0}>
          <SeccionCaja titulo={tMetricas.fase7.avalesTitle} tituloColor="gray.700">
            {d.pulidoFase7.avalesCreadosEnRango.creadosEnRango > 0 ? (
              <>
                <Text fontSize="sm" fontWeight="700" mb={2}>
                  Total: {d.pulidoFase7.avalesCreadosEnRango.creadosEnRango}
                </Text>
                {vistaGraficos ? (
                  <RenderPie
                    data={toPieDataWithKeyColors(
                      d.pulidoFase7.avalesCreadosEnRango.porStatus || {},
                      LABEL_AVAL_STATUS,
                      HEX_AVAL,
                    )}
                    height={200}
                  />
                ) : (
                  <ListDistribAnimada
                    record={d.pulidoFase7.avalesCreadosEnRango.porStatus || {}}
                    labelMap={LABEL_AVAL_STATUS}
                    colorOfKey={() => 'purple.400'}
                    muted={sub}
                  />
                )}
              </>
            ) : (
              <Text fontSize="sm" color={sub}>{tMetricas.fase7.avalesEmpty}</Text>
            )}
          </SeccionCaja>
        </SpotifyScrollSection>
      )}

      {d.pulidoFase7 && (
        <SpotifyScrollSection kicker={s.kickerResenas} expl={s.explResenas} bigNumber={d.resenasBanda?.totalRecibidas ?? 0}>
          <SeccionCaja
            titulo={tMetricas.fase7.resenasTitle}
            tituloColor="gray.700"
            descripcion={tMetricas.fase7.resenasHelp}
            muted={sub}
          >
            {(d.resenasBanda?.totalRecibidas ?? 0) > 0 ? (
              <>
                <HStack flexWrap="wrap" gap={2} mb={3} fontSize="sm">
                  <Text as="span" px={2} py={1} borderRadius="md" bg="whiteAlpha.200" fontWeight="600">
                    Total: {d.resenasBanda?.totalRecibidas}
                  </Text>
                  <Text as="span" px={2} py={1} borderRadius="md" bg="green.800" fontWeight="600">
                    Publicadas: {d.resenasBanda?.publicadas}
                  </Text>
                  <Text as="span" px={2} py={1} borderRadius="md" bg="whiteAlpha.200" fontWeight="600">
                    No publicadas: {d.resenasBanda?.sinPublicar}
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="700" mb={2} color="purple.500">Medias (1–5)</Text>
                <VStack align="stretch" gap={2}>
                  {[
                    ['Comunicación', d.resenasBanda?.promedios?.comunicacion],
                    ['General', d.resenasBanda?.promedios?.general],
                    ['Puntualidad', d.resenasBanda?.promedios?.puntualidad],
                    ['Calidad artística', d.resenasBanda?.promedios?.calidadArtistica],
                  ].map(([label, v], i) => (
                    <MotionBox
                      key={label}
                      initial={{ opacity: 0, y: 6 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ delay: 0.04 * i, duration: 0.35 }}
                    >
                      <Flex
                        justify="space-between"
                        borderRadius="lg"
                        px={3}
                        py={2}
                        bg="whiteAlpha.100"
                        borderLeft="3px solid"
                        borderColor="purple.300"
                      >
                        <Text fontSize="sm" fontWeight="600">{label}</Text>
                        <Text fontSize="sm" fontWeight="800">{v != null ? v : '—'}</Text>
                      </Flex>
                    </MotionBox>
                  ))}
                </VStack>
              </>
            ) : (
              <Text fontSize="sm" color={sub}>{tMetricas.fase7.resenasEmpty}</Text>
            )}
          </SeccionCaja>
        </SpotifyScrollSection>
      )}

      {data.comparativaAnterior && (
        <SpotifyScrollSection kicker={s.kickerComparativa} expl={s.explComparativa} bigNumber={false}>
          <Card.Root bg="whiteAlpha.50" borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="2xl" overflow="hidden">
            <Box h="2px" bgGradient="linear(to-r, purple.400, pink.200)" aria-hidden />
            <Card.Header pb={0} px={{ base: 4, md: 6 }} pt={5}>
              <Text fontWeight="800" fontSize="lg" color="whiteAlpha.900" letterSpacing="-0.02em">
                {tMetricas.informe.comparativaTitulo}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.600" fontWeight="normal" mt={1} lineHeight="tall">
                Periodo de referencia: {formatYmdEs(data.comparativaAnterior.rango.desdeYmd)} —{' '}
                {formatYmdEs(data.comparativaAnterior.rango.hastaYmd)} (misma longitud en días, hacia el pasado).
              </Text>
            </Card.Header>
            <Card.Body overflowX="auto" px={{ base: 4, md: 6 }} pb={6}>
              <Table.Root size="sm" variant="simple" borderRadius="md" overflow="hidden" sx={tableOscuroResumen}>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader color="whiteAlpha.900">Indicador</Table.ColumnHeader>
                    <Table.ColumnHeader color="whiteAlpha.900" textAlign='end'>Periodo anterior</Table.ColumnHeader>
                    <Table.ColumnHeader color="whiteAlpha.900" textAlign='end'>Periodo actual</Table.ColumnHeader>
                    <Table.ColumnHeader color="whiteAlpha.900" textAlign='end'>Variación</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {Object.values(data.comparativaAnterior.kpi).map((row) => (
                    <Table.Row key={row.clave}>
                      <Table.Cell color="whiteAlpha.900">{LABEL_DELTA_KPI[row.clave] || row.clave}</Table.Cell>
                      <Table.Cell color="whiteAlpha.900" textAlign='end'>{row.anterior}</Table.Cell>
                      <Table.Cell color="whiteAlpha.900" textAlign='end'>{row.actual}</Table.Cell>
                      <Table.Cell color="whiteAlpha.900" textAlign='end'>{formatDeltaLine(row)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
              {data.comparativaAnterior.tasaCerrado.actual != null ||
              data.comparativaAnterior.tasaCerrado.anterior != null ? (
                <Text fontSize="sm" mt={4} color="whiteAlpha.700">
                  Tasa «presente / cerrado» (asist. ensayos): anterior{' '}
                  {formatPct(data.comparativaAnterior.tasaCerrado.anterior)} · actual{' '}
                  {formatPct(data.comparativaAnterior.tasaCerrado.actual)}
                  {data.comparativaAnterior.tasaCerrado.diffPuntos != null && (
                    <> (Δ {((data.comparativaAnterior.tasaCerrado.diffPuntos ?? 0) * 100).toFixed(1)} p. p.)</>
                  )}
                </Text>
              ) : null}
            </Card.Body>
          </Card.Root>
        </SpotifyScrollSection>
      )}

      {Array.isArray(d.serieMensual) && (
        <SpotifyScrollSection kicker={s.kickerEvolucion} expl={s.explEvolucion} bigNumber={false}>
          <Card.Root bg="whiteAlpha.50" borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="2xl" overflow="hidden">
            <Box h="2px" bgGradient="linear(to-r, cyan.400, purple.500)" aria-hidden />
            <Card.Header pb={0} px={{ base: 4, md: 6 }} pt={5}>
              <Text fontWeight="800" fontSize="lg" color="whiteAlpha.900" letterSpacing="-0.02em">
                {tMetricas.informe.evolucionTitulo}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.600" fontWeight="normal" mt={1} lineHeight="tall">
                Conteos por mes (mes civil UTC) y los músicos con más «Presente» a ensayos en el rango.
              </Text>
            </Card.Header>
            <Card.Body px={{ base: 4, md: 6 }} pb={6}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6} alignItems="flex-start">
                <Box w="100%">
                  <Text fontSize="sm" fontWeight="700" color="gray.300" mb={2}>
                    Por mes: ensayos y actuaciones
                  </Text>
                  {(() => {
                    const sm = d.serieMensual;
                    const maxB = Math.max(
                      1,
                      ...sm.map((x) =>
                        Math.max(Number(x.ensayos) || 0, Number(x.actuaciones) || 0),
                      ),
                    );
                    return sm.length === 0 ? (
                      <Text fontSize="sm" color={sub}>
                        Sin meses en el rango.
                      </Text>
                    ) : (
                      <Table.Root size="sm" variant="simple">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader color="whiteAlpha.800">Mes</Table.ColumnHeader>
                            <Table.ColumnHeader color="whiteAlpha.800" textAlign='end'>Ens.</Table.ColumnHeader>
                            <Table.ColumnHeader color="whiteAlpha.800" textAlign='end'>Act.</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {sm.map((fila) => {
                            const nEn = Number(fila.ensayos) || 0;
                            const nAct = Number(fila.actuaciones) || 0;
                            const pctE = maxB > 0 ? (nEn / maxB) * 100 : 0;
                            const pctA = maxB > 0 ? (nAct / maxB) * 100 : 0;
                            return (
                              <Table.Row key={fila.mes}>
                                <Table.Cell textTransform="capitalize" color="whiteAlpha.900">{fila.label}</Table.Cell>
                                <Table.Cell textAlign='end'>
                                  <HStack justify="flex-end" gap={2}>
                                    <Box flex={1} maxW="100px" minW="50px">
                                      <Progress.Root
                                        value={Number.isFinite(pctE) ? pctE : 0}
                                        size="sm"
                                        colorPalette="purple"
                                        borderRadius="md"
                                        bg="whiteAlpha.200">
                                        <Progress.Track>
                                          <Progress.Range />
                                        </Progress.Track>
                                      </Progress.Root>
                                    </Box>
                                    <Text as="span" color="whiteAlpha.900" fontWeight="600" minW="2ch" textAlign="end">
                                      {nEn}
                                    </Text>
                                  </HStack>
                                </Table.Cell>
                                <Table.Cell textAlign='end'>
                                  <HStack justify="flex-end" gap={2}>
                                    <Box flex={1} maxW="100px" minW="50px">
                                      <Progress.Root
                                        value={Number.isFinite(pctA) ? pctA : 0}
                                        size="sm"
                                        colorPalette="blue"
                                        borderRadius="md"
                                        bg="whiteAlpha.200">
                                        <Progress.Track>
                                          <Progress.Range />
                                        </Progress.Track>
                                      </Progress.Root>
                                    </Box>
                                    <Text as="span" color="whiteAlpha.900" fontWeight="600" minW="2ch" textAlign="end">
                                      {nAct}
                                    </Text>
                                  </HStack>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    );
                  })()}
                </Box>
                <Box w="100%" overflowX="auto">
                  <Text fontSize="sm" fontWeight="700" color="gray.300" mb={2}>
                    Ranking presentes a ensayos
                  </Text>
                  {(d.rankingPresentesEnsayos || []).length > 0 ? (
                    <Table.Root size="sm" variant="simple">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader color="whiteAlpha.800">#</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.800">Músico</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.800" textAlign='end'>Presente</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {d.rankingPresentesEnsayos.map((m) => (
                          <Table.Row key={m.musicoId}>
                            <Table.Cell color="whiteAlpha.900">{m.posicion}</Table.Cell>
                            <Table.Cell color="whiteAlpha.900">{nombreMusico(m.nombre, m.apellidos)}</Table.Cell>
                            <Table.Cell color="whiteAlpha.900" textAlign='end'>{m.presentes}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  ) : (
                    <Text fontSize="sm" color={sub}>
                      No hay registros de asistencia «Presente» a ensayos en este periodo.
                    </Text>
                  )}
                </Box>
              </SimpleGrid>
              {d.pulidoFase7?.rankingTasaAsistenciaEnsayos && (
                <Box w="100%" mt={6} overflowX="auto">
                  <Text fontSize="sm" fontWeight="700" color="gray.300" mb={1}>
                    {tMetricas.fase7.rankTasaTitle}
                  </Text>
                  <Text fontSize="xs" color={sub} mb={2}>
                    {tMetricas.fase7.rankTasaHelp}
                  </Text>
                  {(d.pulidoFase7.rankingTasaAsistenciaEnsayos || []).length > 0 ? (
                    <Table.Root size="sm" variant="simple">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader color="whiteAlpha.800">#</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.800">Músico</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.800" textAlign='end'>% asist.</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.800" textAlign='end'>Presente</Table.ColumnHeader>
                          <Table.ColumnHeader color="whiteAlpha.800" textAlign='end'>Conv.</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {d.pulidoFase7.rankingTasaAsistenciaEnsayos.map((m) => (
                          <Table.Row key={m.musicoId}>
                            <Table.Cell color="whiteAlpha.900">{m.posicion}</Table.Cell>
                            <Table.Cell color="whiteAlpha.900">{nombreMusico(m.nombre, m.apellidos)}</Table.Cell>
                            <Table.Cell color="whiteAlpha.900" textAlign='end'>{formatPct(m.tasa)}</Table.Cell>
                            <Table.Cell color="whiteAlpha.900" textAlign='end'>{m.presentes}</Table.Cell>
                            <Table.Cell color="whiteAlpha.900" textAlign='end'>{m.convocatorias}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  ) : (
                    <Text fontSize="sm" color={sub}>{tMetricas.fase7.rankTasaEmpty}</Text>
                  )}
                </Box>
              )}
            </Card.Body>
          </Card.Root>
        </SpotifyScrollSection>
      )}
    </VStack>
  );
}
